var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { getSession } from '../session';
import { grantKey } from '../authz';
import { requirementFor } from '../entitlements';
import { t } from '../i18n';
import { button } from '../components/base-buttons';
/*
 * <vitesse-gate-overlay> — the LOCKED-OUT viewport.
 *
 * Where <vitesse-gated> HIDES content and renders a lock in its place, this
 * renders the content BEHIND A BLUR and lays a single clean prompt over it. The
 * user sees the SHAPE of what they are missing — a program that was already
 * extracted, a dashboard that already has data — and one sentence saying why it
 * is out of reach, with the one action that unblocks it.
 *
 * SMALLEST SCOPE. The adopter wraps the smallest region that is actually gated —
 * the viewport under a pill-bar, one panel, one card — not the whole page. The
 * component blurs its OWN box; the chrome around it (the pill bar the user
 * clicked, the header) stays sharp and usable.
 *
 *   <vitesse-pill-tabs ...></vitesse-pill-tabs>
 *   <vitesse-gate-overlay
 *     .rpc=${Program.method.getBlockSource}
 *     .content=${html`<program-view></program-view>`}
 *   ></vitesse-gate-overlay>
 *
 * The gated view is passed as `.content`, NOT as slotted children, and this is
 * deliberate on two counts. First, a light-DOM element (which this is, so the
 * base button + SCSS reach it) has no <slot> projection — rendering replaces its
 * own children — so a wrap-the-children API cannot work here. Second, and more
 * important, the overlay renders `content` ONLY on the allowed path: CSS blur is
 * not access control, so mounting gated content and blurring it would both leak
 * it into the DOM (one devtools toggle from readable) and fire its connect-time
 * fetch. When denied, the reader sees a generic skeleton — a shape, never the
 * data. The HOST must likewise not trigger the view's data fetch while denied;
 * a component that starts its own stream in connectedCallback gets this for free
 * (it never mounts), but a parent-driven load must guard on the same session.
 *
 * TWO REFUSALS, TWO ACTIONS — the load-bearing distinction (docs/gated-ui.md).
 * A refusal is either the org's PLAN (entitlement) or the person's GRANT
 * (permission), and they send the user to completely different places:
 *   - entitlement  → Upgrade (gradient CTA) → emits `vitesse-gate-upgrade`
 *   - permission   → Request access (plain)  → emits `vitesse-gate-request-access`
 * When BOTH refuse, the entitlement wins the framing: buying the tier is the
 * org-level unblock, and the permission ask only makes sense inside a tier that
 * has the feature at all.
 *
 * TRANSPORT-AGNOSTIC. It never calls a backend — it can't, because the three
 * apps reach fleet-control three different ways. It EMITS the intent and the
 * host app routes it (Upgrade → the org-dialog Billing page; Request access →
 * the RequestAccess RPC, which notifies the org's admins). This mirrors how
 * <vitesse-notifications> emits `vitesse-home` and the host navigates.
 *
 * The clean sentence is the denial's OWN `explanation` — the same wording the
 * server refusal carries, so the lock and the eventual error cannot disagree.
 * That string is authored in the trust layer (English there today); the button
 * labels reuse the i18n'd `billing.upgrade` and an overridable `requestLabel`,
 * so no locale file is touched by adding this component.
 *
 * LOAD-BEARING: this is UX, exactly like <vitesse-gated>. The server refuses the
 * call regardless; the overlay's job is that a person is told why — and is
 * offered the way out — before they ever fire a doomed request.
 */
let VitesseGateOverlay = class VitesseGateOverlay extends LitElement {
    constructor() {
        super(...arguments);
        /** Request-access button label. Defaults to English to match the trust layer's
         *  own hardcoded explainer strings; a host may pass an i18n'd value. */
        this.requestLabel = 'Request access';
        /** The confirmation the button becomes after a request is sent. */
        this.requestSentLabel = 'Request sent';
        /** Optimistic: the request-access button flips to a sent confirmation on click. */
        this.requested = false;
        this.tick = 0;
    }
    /** Light DOM so the shared base-ui SCSS + the base button() classes reach it,
     *  same convention as every other base-ui panel. Styles below are injected
     *  once, tag-scoped so they cannot leak. */
    createRenderRoot() {
        ensureStyles();
        return this;
    }
    get #session() {
        return this.session ?? getSession();
    }
    /*
     * The requirement, or NOTHING when an rpc was named that declares none.
     *
     * This used to fall through to the explicit attributes — all undefined for an
     * rpc-driven overlay — which composed an EMPTY requirement, produced no
     * denials, and rendered the content ungated. So `allowsRpc(X)` said no while
     * `<vitesse-gate-overlay .rpc=${X}>` said yes for the same X.
     *
     * Its sibling `vitesse-gated` fixed exactly this and carries the paragraph
     * explaining why; the two live in the same directory and gave opposite answers
     * to one question. The open one is the answer a reviewer never sees, because
     * the control simply works.
     *
     * The attribute path stays for views that compose several calls and so have no
     * single method to point at; it is only reachable when no rpc was given.
     */
    #req() {
        if (this.rpc)
            return requirementFor(this.rpc);
        return { permission: this.permission, capability: this.capability, level: this.level };
    }
    #off;
    connectedCallback() {
        super.connectedCallback();
        this.#off = this.#session?.onChange(() => (this.tick += 1));
    }
    disconnectedCallback() {
        this.#off?.();
        this.#off = undefined;
        super.disconnectedCallback();
    }
    updated(changed) {
        if (changed.has('session')) {
            this.#off?.();
            this.#off = this.#session?.onChange(() => (this.tick += 1));
        }
    }
    render() {
        const session = this.#session;
        // Fail-OPEN: no session (a wiring bug) renders the content ungated rather
        // than hiding a paying customer's features.
        if (!session)
            return this.content ?? html ``;
        const req = this.#req();
        // An rpc that declares no requirement REFUSES, matching Session.allowsRpc and
        // the server. An annotation is how a procedure says who may call it; a
        // procedure that says nothing has not been decided, and rendering it open is
        // the one reading that cannot be corrected later.
        if (!req)
            return this.#undeclared();
        const denials = session.denials(req);
        if (denials.length === 0)
            return this.content ?? html ``;
        // Entitlement wins the framing when both refuse (docs/gated-ui.md "Both at
        // once"): buying the tier is the org-level unblock.
        const money = denials.find((d) => d.entitlement);
        const primary = money ?? denials[0];
        const line = primary.explanation;
        // The full composed suite is the tooltip — the exact server wording, for the
        // person who wants the detail without cluttering the one clean line.
        const detail = session.why(req) ?? line;
        // The blurred layer is a GENERIC skeleton, never the real content. Two
        // reasons, both load-bearing: (1) CSS blur is not redaction — mounting the
        // gated content puts it in the DOM, one devtools toggle from being read, so a
        // permission-gated log tail or program source would leak; (2) mounting it
        // fires its connectedCallback (the doomed subscribe/fetch the overlay exists
        // to prevent). So `content` is rendered ONLY on the allowed path above; here
        // we paint a shape that says "something is here" and nothing more.
        return html `
      <div class="vgate" role="group">
        <div class="vgate-skeleton" aria-hidden="true">
          ${[68, 92, 54, 80, 40].map((w) => html `<div class="vgate-bar" style="width:${w}%"></div>`)}
        </div>
        <div class="vgate-veil" role="note" title=${detail}>
          <div class="vgate-card">
            <vitesse-icon class="vgate-lock" name="lock" size="22"></vitesse-icon>
            <p class="vgate-line">${line}</p>
            ${money ? this.#upgradeButton(primary) : this.#requestButton(primary)}
          </div>
        </div>
      </div>
    `;
    }
    /**
     * An rpc that declares no requirement: locked, and said plainly.
     *
     * The same skeleton the refused path paints, for the same two reasons — the
     * real content is never mounted, so nothing leaks through a blur and no doomed
     * subscribe fires — with wording that points at the procedure rather than at
     * the person, because this is a missing annotation and not a missing grant.
     */
    #undeclared() {
        return html `
      <div class="vgate" role="group">
        <div class="vgate-skeleton" aria-hidden="true">
          ${[68, 92, 54, 80, 40].map((w) => html `<div class="vgate-bar" style="width:${w}%"></div>`)}
        </div>
        <div class="vgate-veil" role="note" title="this action declares no authorization requirement">
          <div class="vgate-card">
            <vitesse-icon class="vgate-lock" name="lock" size="22"></vitesse-icon>
            <!-- English, like the rest of this component's refusal copy. That it is
                 English-only is a real gap and its own item; inventing a single
                 i18n key here would leave four locales falling back and read as
                 solved. -->
            <p class="vgate-line">This action is not available.</p>
          </div>
        </div>
      </div>
    `;
    }
    /** The gradient Upgrade CTA — the tier-coloured call the claim flow uses for
     *  its own headline action. Emits the intent; the host opens billing. */
    #upgradeButton(d) {
        return html `<button
      class="base-button vgate-upgrade"
      @click=${() => this.dispatchEvent(new CustomEvent('vitesse-gate-upgrade', {
            detail: { feature: d.missing },
            bubbles: true,
            composed: true,
        }))}
    >
      <vitesse-icon name="sparkles" size="15"></vitesse-icon>
      <span>${t('billing.upgrade')}</span>
    </button>`;
    }
    /** The plain Request-access button — a person asking their admin for a grant.
     *  Emits the permission + context; the host calls RequestAccess, which
     *  notifies the org's admins (they act on it from the RBAC panel). Flips to a
     *  local "sent" confirmation optimistically — the ask is fire-and-forget from
     *  the requester's side, and the host toasts if the call actually fails. */
    #requestButton(d) {
        if (this.requested) {
            return html `<span class="vgate-sent"><vitesse-icon name="check" size="15"></vitesse-icon>${this.requestSentLabel}</span>`;
        }
        return button({
            label: this.requestLabel,
            icon: 'shield-user',
            color: 'blue',
            onClick: () => {
                this.requested = true;
                this.dispatchEvent(new CustomEvent('vitesse-gate-request-access', {
                    // grantKey, not a regex. The stored-key form is a shared spelling with a
                    // shared function, and re-deriving it here is a second definition that
                    // agrees only for as long as nobody changes the prefix rule.
                    detail: { permission: grantKey(d.missing), context: this.context ?? '' },
                    bubbles: true,
                    composed: true,
                }));
            },
        });
    }
};
__decorate([
    property({ attribute: false })
], VitesseGateOverlay.prototype, "session", void 0);
__decorate([
    property({ attribute: false })
], VitesseGateOverlay.prototype, "rpc", void 0);
__decorate([
    property({ type: String })
], VitesseGateOverlay.prototype, "permission", void 0);
__decorate([
    property({ type: String })
], VitesseGateOverlay.prototype, "capability", void 0);
__decorate([
    property({ type: String })
], VitesseGateOverlay.prototype, "level", void 0);
__decorate([
    property({ type: String })
], VitesseGateOverlay.prototype, "context", void 0);
__decorate([
    property({ type: String })
], VitesseGateOverlay.prototype, "requestLabel", void 0);
__decorate([
    property({ type: String })
], VitesseGateOverlay.prototype, "requestSentLabel", void 0);
__decorate([
    state()
], VitesseGateOverlay.prototype, "requested", void 0);
__decorate([
    property({ attribute: false })
], VitesseGateOverlay.prototype, "content", void 0);
__decorate([
    state()
], VitesseGateOverlay.prototype, "tick", void 0);
VitesseGateOverlay = __decorate([
    customElement('vitesse-gate-overlay')
], VitesseGateOverlay);
export { VitesseGateOverlay };
// Injected ONCE, tag-scoped so nothing leaks — the light-DOM equivalent of a
// shadow stylesheet, kept in this file so the component's whole footprint is one
// file plus one export line.
let stylesInjected = false;
function ensureStyles() {
    if (stylesInjected || typeof document === 'undefined')
        return;
    stylesInjected = true;
    const style = document.createElement('style');
    style.id = 'vitesse-gate-overlay-styles';
    style.textContent = `
    vitesse-gate-overlay { display: block; position: relative; }
    vitesse-gate-overlay .vgate { position: relative; min-height: 8rem; }
    /* A generic, blurred SHAPE — never the real content (see the class comment).
       Rows of faint bars read as "there is something here" without carrying any
       actual data into the DOM. */
    vitesse-gate-overlay .vgate-skeleton {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      padding: var(--space-5, 1.25rem);
      filter: blur(6px);
      opacity: 0.5;
      pointer-events: none;
      user-select: none;
    }
    vitesse-gate-overlay .vgate-bar {
      height: 0.85rem;
      border-radius: 6px;
      background: var(--border-color);
    }
    vitesse-gate-overlay .vgate-veil {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--space-4, 1rem);
      background: color-mix(in srgb, var(--surface-color, var(--surface)) 30%, transparent);
    }
    vitesse-gate-overlay .vgate-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--space-3, 0.75rem);
      max-width: 34ch;
      padding: var(--space-5, 1.25rem) var(--space-6, 1.5rem);
      text-align: center;
      border: 1px solid var(--border-color);
      border-radius: var(--radius-lg, 12px);
      background: var(--surface-raised);
      box-shadow: 0 8px 30px color-mix(in srgb, var(--shadow-color) 22%, transparent);
    }
    vitesse-gate-overlay .vgate-lock { color: var(--text-secondary); }
    vitesse-gate-overlay .vgate-sent {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      font-size: var(--font-size-sm, 0.875rem);
      color: var(--text-secondary);
    }
    vitesse-gate-overlay .vgate-sent vitesse-icon { color: var(--success-color, #22c55e); }
    vitesse-gate-overlay .vgate-line {
      margin: 0;
      font-size: var(--font-size-sm, 0.875rem);
      line-height: 1.45;
      color: var(--text-primary);
    }
    /* The gradient Upgrade CTA — a base button whose fill is the tier gradient,
       everything else inherited from .base-button so it stays one affordance. */
    vitesse-gate-overlay .vgate-upgrade {
      background: linear-gradient(100deg, #2fd4de 0%, #d65cf0 50%, #ffab3d 100%);
      background-size: 200% 100%;
      color: #fff;
      border: none;
      transition: background-position 0.5s ease, transform 0.1s ease;
    }
    vitesse-gate-overlay .vgate-upgrade:hover { background-position: 100% 0; }
    vitesse-gate-overlay .vgate-upgrade:active { transform: translateY(1px); }
    vitesse-gate-overlay .vgate-upgrade vitesse-icon { color: #fff; }
  `;
    document.head.appendChild(style);
}
//# sourceMappingURL=vitesse-gate-overlay.js.map